import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { authApi } from '../api/auth';
import { useAuth } from '../context/AuthContext';

// Same desktop-detection LoginPage.tsx uses — the packaged desktop app has
// no marketing site (coreledger.in) to link back to.
const IS_DESKTOP_APP = import.meta.env.VITE_USE_HASH_ROUTER === 'true';

export default function RegisterPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: '', email: '', password: '', confirm: '', companyName: '', gstin: '' });
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  function set(k: keyof typeof form) {
    return (e: React.ChangeEvent<HTMLInputElement>) => setForm(f => ({ ...f, [k]: e.target.value }));
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (form.password !== form.confirm) { setError('Passwords do not match'); return; }
    if (form.password.length < 8) { setError('Password must be at least 8 characters'); return; }
    setError(null);
    setLoading(true);
    try {
      const res = await authApi.register({ name: form.name, email: form.email.trim(), password: form.password, companyName: form.companyName, gstin: form.gstin || undefined });
      login(res.accessToken, res.user);
      navigate('/invoices');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Registration failed');
    } finally {
      setLoading(false);
    }
  }

  const lbl: React.CSSProperties = { display: 'grid', gap: '0.3rem', marginBottom: '0.85rem', fontSize: '0.82rem', fontWeight: 500, color: '#374151' };

  return (
    <div style={{ minHeight: '100vh', background: 'linear-gradient(135deg,#1e3a5f 0%,#2563eb 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem' }}>
      <div style={{ background: 'white', borderRadius: 20, padding: '2.5rem', width: '100%', maxWidth: 460, boxShadow: '0 20px 60px rgba(0,0,0,0.25)' }}>
        <div style={{ textAlign: 'center', marginBottom: '1.75rem' }}>
          {/* Links back to the marketing site — see LoginPage.tsx's own
              identical block for the full explanation. */}
          {IS_DESKTOP_APP ? (
            <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>📊</div>
          ) : (
            <a href="https://coreledger.in" style={{ textDecoration: 'none', display: 'block' }} title="Back to coreledger.in">
              <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>📊</div>
            </a>
          )}
          <h1 style={{ fontSize: '1.4rem', fontWeight: 800, color: '#1e3a5f' }}>Create your account</h1>
          <p style={{ color: '#64748b', fontSize: '0.85rem', marginTop: '0.25rem' }}>Start billing in minutes</p>
        </div>

        {error && <div className="error-box">{error}</div>}

        <form onSubmit={handleSubmit}>
          <div className="form-row form-row-2" style={{ gap: '0.75rem', marginBottom: 0 }}>
            <label style={lbl}>Your name<input value={form.name} onChange={set('name')} placeholder="Raj Kumar" required autoFocus /></label>
            <label style={lbl}>Email<input type="email" value={form.email} onChange={set('email')} placeholder="raj@company.com" required /></label>
          </div>
          <label style={lbl}>Company name<input value={form.companyName} onChange={set('companyName')} placeholder="Acme Traders Pvt Ltd" required /></label>
          <label style={lbl}>GSTIN (optional)<input value={form.gstin} onChange={set('gstin')} placeholder="22AAAAA0000A1Z5" maxLength={15} /></label>
          <div className="form-row form-row-2" style={{ gap: '0.75rem', marginBottom: 0 }}>
            <label style={lbl}>Password<input type="password" value={form.password} onChange={set('password')} placeholder="Min 8 chars" required /></label>
            <label style={lbl}>Confirm<input type="password" value={form.confirm} onChange={set('confirm')} placeholder="Confirm password" required /></label>
          </div>
          <button type="submit" className="btn btn-primary" style={{ width: '100%', justifyContent: 'center', padding: '0.75rem', marginTop: '0.5rem' }} disabled={loading}>
            {loading ? <span className="spinner" style={{ width: 16, height: 16 }} /> : 'Create Account →'}
          </button>
        </form>

        <p style={{ textAlign: 'center', marginTop: '1.25rem', fontSize: '0.85rem', color: '#64748b' }}>
          Already have an account?{' '}
          <Link to="/login" style={{ color: '#2563eb', fontWeight: 600 }}>Sign in</Link>
        </p>
      </div>
    </div>
  );
}
